import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, Container, Typography } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DynamicContent from '../ClerkVending/DynamicContent';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';

import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { sendNotification } from 'src/utils/sendNotification';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import Content from '../Solicitor/Content';
import ApplicationStatus from 'src/ui-component/ApplicationStatus';
import { getApplicationDetails } from '../common/getApplicationDetails';

interface ApplicationDataTypes {
  editApplicationFormData: {
    BusinessName: string;
    BusinessAddress: string;
    OwnerName: string;
    OwnerAddress: string;
    ServiceNumber: string;
    ApplicantPhoneNo: string;
    ApplicantAddress: string;
    ApplicantName: string;
  };
}

let editData: any;
const EditApplication: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation()
  const navigate = useNavigate();
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const socket = useSelector((store: RootState) => store.socket.socket);
  const isManager = currentPermitRole === 'Manager';
  const { townshipId, permit } = useParams();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [ApplicationFee, setApplicationFee] = useState<string>('');
  const [switchTo, setSwitchTo] = useState<string>('');
  const [dynamicRows, setDynamicRows] = useState<any[]>();
  const [categoryError, setCategoryError] = useState<string>('');
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState<number>(0);
  const { applicationID } = useParams();
  const formRef = useRef(null);
  const [applicationDetails, setApplicationDetails] = useState<
    ApplicationDataTypes['editApplicationFormData']
  >({
    BusinessName: editData?.BusinessName,
    BusinessAddress: editData?.BusinessAddress,
    OwnerName: editData?.OwnerName,
    OwnerAddress: editData?.OwnerAddress,
    ServiceNumber: editData?.ServiceNumber,
    ApplicantPhoneNo: editData?.ApplicantPhoneNo,
    ApplicantAddress: editData?.ApplicantAddress,
    ApplicantName: editData?.ApplicantName
  });

  //Method to get application details
  const handleGetApplicationData = async () => {
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft
    const response = await getApplicationDetails({ applicationID, permit, isDraft })
    if (response?.status === "success") {
      editData =response?.data?.data[0]
      setApplicationDetails(response?.data?.data[0])
    }
  }

  //Method to handle dependency
  useEffect(() => {
    handleGetApplicationData()
  }, [applicationID, permit]);

  function handleDependency(val: string, { name }) {
    const data = { ...applicationDetails };
    data[name] = val;
    setApplicationDetails(data);
  }

  //Fields for the form using Formik
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.ApplicantName,
      dependency: handleDependency
    },
    {
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.ApplicantAddress,
      dependency: handleDependency
    },
    {
      fieldName: 'ApplicantPhoneNo',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      required: true,
      initialValue: applicationDetails.ApplicantPhoneNo,
      dependency: handleDependency
    },
    {
      fieldName: 'BusinessName',
      fieldLabel: 'Name of business concern',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.BusinessName,
      dependency: handleDependency
    },
    {
      fieldName: 'BusinessAddress',
      fieldLabel: 'Business Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.BusinessAddress,
      dependency: handleDependency
    },

    {
      fieldName: 'OwnerName',
      fieldLabel: 'Owner Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.OwnerName || ''
    },
    {
      fieldName: 'OwnerAddress',
      fieldLabel: 'Owner Address ',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.OwnerAddress || ''
    },

    {
      fieldName: 'ServiceNumber',
      fieldLabel: 'Service Number',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.ServiceNumber || ''
    }
  ];
  //Fetch application details
  const handleGetApplicationDetails = async () => {
    try {
      const response = await httpRequest(
        'GET',
        `/clerk_vending/getByApplication/${applicationID}`
      );

      const fetchedCategories =
        response?.data?.ApplicationID?.vendingMachines || [];
      const totalFee = response?.data?.ApplicationID?.ApplicationFee || '';
      setApplicationFee(totalFee);
      setDynamicRows(fetchedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  //Method to get application details
  useEffect(() => {
    handleGetApplicationDetails();
  }, []);

  //Calculate total fee
  function totalFeeCalculate() {
    let initialVal = 0;
    dynamicRows.forEach((item) => {
      initialVal += Number(item.Fee);
    });
    setFeeDetails((prevState) => ({
      ...prevState,
      applicationFee: Number(initialVal)
    }));
    return initialVal.toFixed(2);
  }

  //Method to proceed for payment when draft
  const handleProceedToPay = async (data: any) => {
    setLoading(true);
    const response = await httpRequest(
      'PATCH',
      `/clerk_vending/update/${editData.ApplicationID}`,
      data
    );
    if (response?.status === 'success') {
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };

      paymentSummary.fees.applicationFee = Number(totalFeeCalculate());
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: editData?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'Vending'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to update application
  const handleUpdateApplication = async (data: any, type = '') => {
    setLoading(true);
    const response = await httpRequest(
      'PATCH',
      `/clerk_vending/update/${editData.ApplicationID}`,
      data
    );
    if (response?.status === 'success') {
      if (type) return true;
      toast.success(`Application update successfully.`);
      setLoading(false);
      if (editData.ApplicationStatus === 'Draft')
        navigate(`/${townshipId}/Clerk Department/${permit}/draft-application`);
      else
        navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to Approve the application
  const handleApproveApplication = async () => {
    setLoading(true);
    const payload = {
      ApplicationID: Number(editData.ApplicationID),
      ApplicationStatus: 'Approved',
      BusinessAddress: applicationDetails.BusinessAddress,
      BusinessName: applicationDetails.BusinessName,
      ServiceNumber: applicationDetails.ServiceNumber,
      OwnerName: applicationDetails.OwnerName,
      OwnerAddress: applicationDetails.OwnerAddress
    };
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );
    if (response?.status === 'success') {
      sendNotification(socket, {
        UserID: editData?.UserID,
        TownshipID: townshipId,
        Title: 'Application Approved',
        Message: `Clerk vending application (${editData?.ApplicationNumber}) is approved.`,
        ApplicationNumber: editData?.ApplicationNumber
      });
      toast?.success(`Application Approved successfully.`);
      navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };
  const handleValidation = async () => {
    const hasCategory = dynamicRows.some(
      (row) => row.MachineName && row.Location && row.Fee > 0
    );

    const totalFees = dynamicRows.reduce((total, row) => total + row.Fee, 0);

    if (!hasCategory || totalFees <= 0) {
      setCategoryError('Please add at least one category and location.');
      return;
    }

    setCategoryError('');
  };

  //Method to handle the application action(Payment,Update,Approve)
  const handleSubmit = async (data) => {
    handleValidation();
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(data);
    } else if (clickedOn === 'Update') {
      handleUpdateApplication(data);
    } else if (clickedOn === 'Approve') {
      const applicationUpdated = await handleUpdateApplication(data, 'Approve');
      if (applicationUpdated) handleApproveApplication();
    }
  };

  //Get Creadit card application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Get application fee
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
    return () => {
      editData = '';
    };
  }, []);

  //Get form buttons
  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (editData?.ApplicationStatus) {
      if (
        editData?.ApplicationStatus === 'In Review' ||
        editData?.ApplicationStatus === 'Draft'
      ) {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (editData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Proceed to Payment'];
      }
      if (isManager && editData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Approve'];
      }
    }
    return buttonsCollection;
  };
  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container className="main-table-container" sx={{ mt: 2, pb:{xs:"72px !important", sm:"auto"} }}>
      <Breadcrumb PermitName={'Clerk Vending'} PageTitle={'Edit Application'} />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="edit"
        applicationStatus={editData?.ApplicationStatus}
        applicationNumber={editData?.ApplicationNumber}
      />
      <Card>
        <>
          {switchTo === '' ? (
            <Box
              m={{ md: 3, xs: 1 }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              {editData && editData?.ApplicationStatus !== 'Draft' ? (
                <ApplicationStatus editAppStatus={editData} />
              ) : (
                <Typography variant="h4">Edit Application</Typography>
              )}

              <PdfGenerator
                PermitName={'Clerk Amusement/Vending Licenses'}
                allFields={allFields}
              />
            </Box>
          ) : null}
          <Box m={{ md: 3, xs: 1 }}>
            {switchTo !== '' ? (
              <EditFormfeatureViews
                switchTo={switchTo}
                setSwitchTo={setSwitchTo}
                applicationDetails={{
                  TownshipID: townshipId,
                  applicationID: applicationID,
                  UserID: editData?.UserID,
                  ApplicationStatus: editData?.ApplicationStatus
                }}
              />
            ) : (
              <>
                <FormikForm
                  allFields={allFields}
                  buttonTitle={
                    editData
                      ? getFormButtons()
                      : ['Cancel', 'Save Draft', 'Proceed to Payment']
                  }
                  formMethod={handleSubmit}
                  buttonPlacement={'right'}
                  setClickedOn={setClickedOn}
                  innerRef={formRef}
                  clickProp
                  content={
                    <>
                      <DynamicContent
                        dynamicRows={dynamicRows}
                        setDynamicRows={setDynamicRows}
                        categoryError={categoryError}
                        setCategoryError={setCategoryError}
                        sendTotalFee={() => { }}
                        ApplicationFee={ApplicationFee}
                        setApplicationFee={setApplicationFee}
                        ApplicationStatus={editData?.ApplicationStatus}
                        applicationID={applicationID}
                        handleGetApplicationDetails={
                          handleGetApplicationDetails
                        }
                      />
                      <Content
                        switchTo={switchTo}
                        setSwitchTo={setSwitchTo}
                        applicationID={editData?.ApplicationID}
                        UserID={userData?.UserID}
                        ApplicationStatus={editData?.ApplicationStatus}
                        ApplicationNumber={editData?.ApplicationNumber}
                        selectMultipleFiles={false}
                      />
                    </>
                  }
                  defaultEnabledButton={[
                    'Proceed to Payment',
                    'Approve',
                    'Update'
                  ]}
                  handleOtherButton={() => {
                    navigate(
                      `/${townshipId}/Clerk Department/${permit}/${editData?.ApplicationStatus === 'Draft'
                        ? 'draft-application'
                        : 'application-list'
                      }`
                    );
                  }}
                />
              </>
            )}
          </Box>
        </>
      </Card>
    </Container>
  );
};

export default EditApplication;
